/**
 * VALIDATION SCHEMAS
 * ==================
 * 
 * Centralized validation logic for forms and data.
 * Uses Zod for type-safe validation with Supabase compatibility.
 * 
 * Install zod with: npm install zod
 */

// Note: Zod is not installed yet. These are the schemas we'll need.
// For now, we'll provide native JavaScript validation functions.

import { VALIDATION, RATING_SCALE } from '../constants'

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Check if a string is a valid email
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

/**
 * Check if a string is a valid UUID
 */
export function isValidUUID(id: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(id)
}

/**
 * Check if a date string is valid ISO format
 */
export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString)
  return !isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0]
}

/**
 * Check if a rating is within valid range
 */
export function isValidRating(rating: number): boolean {
  return Number.isInteger(rating) && rating >= RATING_SCALE.MIN && rating <= RATING_SCALE.MAX
}

// ============================================================================
// ENHANCED VALIDATION FUNCTIONS
// ============================================================================

/**
 * Enhanced rating validation with detailed error messages
 */
export function validateRatingInput(input: unknown): { isValid: boolean; error?: string } {
  // Handle null/undefined
  if (input === null || input === undefined) {
    return { isValid: false, error: 'Rating is required' }
  }

  // Handle string inputs
  if (typeof input === 'string') {
    const trimmed = input.trim()
    if (trimmed === '') {
      return { isValid: false, error: 'Rating is required' }
    }
    
    // Check for non-numeric characters
    if (!/^\d+\.?\d*$/.test(trimmed)) {
      return { isValid: false, error: 'Please enter a number between 1 and 10' }
    }
    
    const parsed = parseFloat(trimmed)
    if (isNaN(parsed)) {
      return { isValid: false, error: 'Please enter a valid number' }
    }
    
    input = parsed
  }

  // Convert to number if needed
  const rating = Number(input)
  
  if (isNaN(rating)) {
    return { isValid: false, error: 'Please enter a valid number' }
  }

  // Check for decimal values
  if (!Number.isInteger(rating)) {
    return { isValid: false, error: 'Please enter a whole number between 1 and 10' }
  }

  // Check range
  if (rating < RATING_SCALE.MIN) {
    return { isValid: false, error: `Rating must be at least ${RATING_SCALE.MIN}` }
  }
  
  if (rating > RATING_SCALE.MAX) {
    return { isValid: false, error: `Rating must be ${RATING_SCALE.MAX} or less` }
  }

  return { isValid: true }
}

/**
 * Enhanced decision date validation
 */
export function validateDecisionDate(dateInput: string): { isValid: boolean; error?: string; warning?: string } {
  if (!dateInput || dateInput.trim() === '') {
    return { isValid: false, error: 'Decision date is required' }
  }

  // Check basic date format
  const date = new Date(dateInput)
  if (isNaN(date.getTime())) {
    return { isValid: false, error: 'Please enter a valid date in YYYY-MM-DD format' }
  }

  // Check if date string matches expected format
  if (dateInput !== date.toISOString().split('T')[0]) {
    return { isValid: false, error: 'Invalid date format. Please use YYYY-MM-DD format' }
  }

  const today = new Date()
  const maxFutureDate = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
  const maxPastDate = new Date(today.getTime() - 2 * 365 * 24 * 60 * 60 * 1000) // 2 years ago

  // Check for unrealistic future dates
  if (date > maxFutureDate) {
    return { isValid: false, error: 'Decision date cannot be more than 7 days in the future' }
  }

  // Warn about dates far in the past
  if (date < maxPastDate) {
    return { 
      isValid: true, 
      warning: 'This date is over 2 years ago. Is this correct?' 
    }
  }

  return { isValid: true }
}

/**
 * Enhanced review interval validation
 */
export function validateReviewIntervals(intervals: number[]): { isValid: boolean; errors: string[]; warnings: string[] } {
  const errors: string[] = []
  const warnings: string[] = []

  if (!Array.isArray(intervals)) {
    errors.push('Review intervals must be provided as an array')
    return { isValid: false, errors, warnings }
  }

  if (intervals.length === 0) {
    errors.push('At least one review interval is required')
    return { isValid: false, errors, warnings }
  }

  // Validate each interval
  const validIntervals = intervals.filter(interval => {
    if (!Number.isInteger(interval)) {
      errors.push('Review intervals must be whole numbers')
      return false
    }
    
    if (interval <= 0) {
      errors.push('Review intervals must be positive (1 or more days)')
      return false
    }
    
    if (interval > 365) {
      errors.push('Review intervals cannot exceed 365 days')
      return false
    }
    
    return true
  })

  // Check for duplicates
  const duplicates = validIntervals.filter((interval, index) => 
    validIntervals.indexOf(interval) !== index
  )
  if (duplicates.length > 0) {
    errors.push(`Duplicate review intervals found: ${duplicates.join(', ')}`)
  }

  // Check for intervals too close together and ensure sequential order
  const sorted = [...validIntervals].sort((a, b) => a - b)
  
  // Validate that intervals are in sequential order (ascending)
  const originalOrder = [...validIntervals]
  for (let i = 1; i < originalOrder.length; i++) {
    if (originalOrder[i] <= originalOrder[i - 1]) {
      errors.push(`Review intervals must be in sequential order (${originalOrder[i - 1]} days should come before ${originalOrder[i]} days)`)
    }
  }
  
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] < 3) {
      warnings.push(`Reviews on day ${sorted[i - 1]} and ${sorted[i]} are less than 3 days apart`)
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  }
}

/**
 * Calculate and validate due dates
 */
export function validateDueDateCalculation(decisionDate: string, intervals: number[]): {
  isValid: boolean
  errors: string[]
  warnings: string[]
  dueDates: Array<{ day: number; date: string; dayOfWeek: string }>
} {
  const errors: string[] = []
  const warnings: string[] = []
  const dueDates: Array<{ day: number; date: string; dayOfWeek: string }> = []

  if (!decisionDate) {
    errors.push('Decision date is required for due date calculation')
    return { isValid: false, errors, warnings, dueDates }
  }

  const baseDate = new Date(decisionDate)
  if (isNaN(baseDate.getTime())) {
    errors.push('Invalid decision date for due date calculation')
    return { isValid: false, errors, warnings, dueDates }
  }

  const maxFutureDate = new Date(baseDate.getTime() + 2 * 365 * 24 * 60 * 60 * 1000) // 2 years from decision

  intervals.forEach(interval => {
    const dueDate = new Date(baseDate.getTime() + interval * 24 * 60 * 60 * 1000)
    
    if (dueDate > maxFutureDate) {
      errors.push(`Review interval ${interval} days results in a due date more than 2 years in the future`)
      return
    }

    const dayOfWeek = dueDate.toLocaleDateString('en-US', { weekday: 'long' })
    const dateStr = dueDate.toISOString().split('T')[0]
    
    dueDates.push({
      day: interval,
      date: dateStr,
      dayOfWeek
    })

    // Warn about weekend reviews
    if (dayOfWeek === 'Saturday' || dayOfWeek === 'Sunday') {
      warnings.push(`Review on day ${interval} falls on a ${dayOfWeek} (${dateStr})`)
    }
  })

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    dueDates
  }
}

// ============================================================================
// DECISION VALIDATION
// ============================================================================

export interface DecisionValidationResult {
  isValid: boolean
  errors: Record<string, string>
}

/**
 * Validate decision creation data
 */
export function validateDecision(data: {
  title: string
  description?: string
  rationale?: string
  context?: string
  decision_date: string
  day0_rating: number
  review_intervals: number[]
}): DecisionValidationResult {
  const errors: Record<string, string> = {}

  // Title validation
  if (!data.title || data.title.trim().length === 0) {
    errors.title = 'Decision title is required'
  } else if (data.title.trim().length < VALIDATION.DECISION_TITLE.MIN_LENGTH) {
    errors.title = `Title must be at least ${VALIDATION.DECISION_TITLE.MIN_LENGTH} characters`
  } else if (data.title.trim().length > VALIDATION.DECISION_TITLE.MAX_LENGTH) {
    errors.title = `Title must be no more than ${VALIDATION.DECISION_TITLE.MAX_LENGTH} characters`
  }

  // Description validation (optional)
  if (data.description && data.description.length > VALIDATION.DECISION_DESCRIPTION.MAX_LENGTH) {
    errors.description = `Description must be no more than ${VALIDATION.DECISION_DESCRIPTION.MAX_LENGTH} characters`
  }

  // Decision date validation
  if (!data.decision_date) {
    errors.decision_date = 'Decision date is required'
  } else if (!isValidDate(data.decision_date)) {
    errors.decision_date = 'Invalid date format'
  }

  // Rating validation
  if (!isValidRating(data.day0_rating)) {
    errors.day0_rating = `Rating must be between ${RATING_SCALE.MIN} and ${RATING_SCALE.MAX}`
  }

  // Review intervals validation
  if (!Array.isArray(data.review_intervals) || data.review_intervals.length === 0) {
    errors.review_intervals = 'At least one review interval is required'
  } else {
    const invalidIntervals = data.review_intervals.filter(interval => 
      !Number.isInteger(interval) || interval <= 0
    )
    if (invalidIntervals.length > 0) {
      errors.review_intervals = 'Review intervals must be positive integers'
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

// ============================================================================
// RATING VALIDATION
// ============================================================================

/**
 * Validate rating submission data
 */
export function validateRating(data: {
  decision_id: string
  review_day: number
  rating: number
  notes?: string
}): DecisionValidationResult {
  const errors: Record<string, string> = {}

  // Decision ID validation
  if (!data.decision_id || !isValidUUID(data.decision_id)) {
    errors.decision_id = 'Invalid decision ID'
  }

  // Review day validation
  if (!Number.isInteger(data.review_day) || data.review_day < 0) {
    errors.review_day = 'Review day must be a non-negative integer'
  }

  // Rating validation
  if (!isValidRating(data.rating)) {
    errors.rating = `Rating must be between ${RATING_SCALE.MIN} and ${RATING_SCALE.MAX}`
  }

  // Notes validation (optional)
  if (data.notes && data.notes.length > VALIDATION.RATING_NOTES.MAX_LENGTH) {
    errors.notes = `Notes must be no more than ${VALIDATION.RATING_NOTES.MAX_LENGTH} characters`
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

// ============================================================================
// USER PROFILE VALIDATION
// ============================================================================

/**
 * Validate user profile data
 */
export function validateProfile(data: {
  full_name?: string
  email: string
}): DecisionValidationResult {
  const errors: Record<string, string> = {}

  // Email validation
  if (!data.email) {
    errors.email = 'Email is required'
  } else if (!isValidEmail(data.email)) {
    errors.email = 'Invalid email format'
  }

  // Full name validation (optional)
  if (data.full_name && data.full_name.trim().length > 100) {
    errors.full_name = 'Full name must be no more than 100 characters'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

// ============================================================================
// TAG VALIDATION
// ============================================================================

/**
 * Validate tag name
 */
export function validateTagName(name: string): { isValid: boolean; error?: string } {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: 'Tag name is required' }
  }

  if (name.trim().length < VALIDATION.TAG_NAME.MIN_LENGTH) {
    return { isValid: false, error: `Tag name must be at least ${VALIDATION.TAG_NAME.MIN_LENGTH} character` }
  }

  if (name.trim().length > VALIDATION.TAG_NAME.MAX_LENGTH) {
    return { isValid: false, error: `Tag name must be no more than ${VALIDATION.TAG_NAME.MAX_LENGTH} characters` }
  }

  // Check for valid characters (alphanumeric, spaces, hyphens, underscores)
  const validTagRegex = /^[a-zA-Z0-9\s\-_]+$/
  if (!validTagRegex.test(name.trim())) {
    return { isValid: false, error: 'Tag name can only contain letters, numbers, spaces, hyphens, and underscores' }
  }

  return { isValid: true }
}

// ============================================================================
// PASSWORD VALIDATION
// ============================================================================

/** The five requirements a password is scored against. */
export interface PasswordRequirements {
  length: boolean
  uppercase: boolean
  lowercase: boolean
  number: boolean
  special: boolean
}

/**
 * Score a password against the sign-up requirements.
 *
 * `score` counts how many of the five are met. Sign-up accepts 3 of 5, and
 * changing a password in Settings applies the same bar so a password accepted
 * at sign-up cannot be rejected later, or vice versa.
 */
export function getPasswordStrength(password: string): {
  requirements: PasswordRequirements
  score: number
} {
  const requirements: PasswordRequirements = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  }

  const score = Object.values(requirements).filter(Boolean).length
  return { requirements, score }
}

/** Minimum `getPasswordStrength` score accepted when setting a password. */
export const MIN_PASSWORD_STRENGTH_SCORE = 3

/**
 * Validate password strength
 */
export function validatePassword(password: string): DecisionValidationResult {
  const errors: Record<string, string> = {}

  if (!password) {
    errors.password = 'Password is required'
  } else {
    if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters long'
    }

    if (!/(?=.*[a-z])/.test(password)) {
      errors.password = 'Password must contain at least one lowercase letter'
    }

    if (!/(?=.*[A-Z])/.test(password)) {
      errors.password = 'Password must contain at least one uppercase letter'
    }

    if (!/(?=.*\d)/.test(password)) {
      errors.password = 'Password must contain at least one number'
    }

    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.password = 'Password must contain at least one special character'
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

// ============================================================================
// FORM UTILITIES
// ============================================================================

/**
 * Generic form field validator
 */
export function validateField(
  value: unknown,
  rules: {
    required?: boolean
    minLength?: number
    maxLength?: number
    pattern?: RegExp
    custom?: (value: unknown) => string | null
  }
): string | null {
  // Required check
  if (rules.required && (!value || (typeof value === 'string' && value.trim().length === 0))) {
    return 'This field is required'
  }

  // Skip other validations if value is empty and not required
  if (!value || (typeof value === 'string' && value.trim().length === 0)) {
    return null
  }

  // String validations
  if (typeof value === 'string') {
    if (rules.minLength && value.trim().length < rules.minLength) {
      return `Must be at least ${rules.minLength} characters`
    }

    if (rules.maxLength && value.trim().length > rules.maxLength) {
      return `Must be no more than ${rules.maxLength} characters`
    }

    if (rules.pattern && !rules.pattern.test(value)) {
      return 'Invalid format'
    }
  }

  // Custom validation
  if (rules.custom) {
    return rules.custom(value)
  }

  return null
}

/**
 * Validate entire form object
 */
export function validateForm<T extends Record<string, unknown>>(
  data: T,
  schema: Record<keyof T, Parameters<typeof validateField>[1]>
): DecisionValidationResult {
  const errors: Record<string, string> = {}

  for (const [field, rules] of Object.entries(schema)) {
    const error = validateField(data[field], rules)
    if (error) {
      errors[field] = error
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}